<template>
  <div class="min-h-full flex flex-col">
    <AnnouncementBar />
    <AnnouncementModal />
    <div class="flex-1 p-4">
    <div v-if="message" :class="['fixed top-4 left-1/2 transform -translate-x-1/2 z-[10010] px-6 py-3 rounded-xl shadow-lg text-white font-semibold transition-all', messageType === 'error' ? 'bg-red-500' : 'bg-blue-600']">
      {{ message }}
    </div>
    <AuthModal
      v-model="showAuthModal"
      :initial-tab="authInitialTab"
      :redirect-after-auth="authRedirectPath"
      @success="onAuthSuccess"
    />

    <!-- Payment Dialog -->
    <div v-if="showPaymentDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div class="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl text-center">
        <div class="text-center mb-6">
          <div class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <IconHash :size="32" class="text-blue-600" />
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">使用支付宝付款</h3>
          <p class="text-gray-600">您的文件包含 {{ fileRowCount }} 行数据，需要支付 ¥{{ paymentPrice }} 元</p>
          <p class="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2 mt-2 text-left">
            请先使用优惠码再前往付款；若先付款再填优惠码，支付金额可能与订单不一致。
          </p>
        </div>
        
        <div class="space-y-4">
          <div v-if="!batchPromoApplied" class="text-left space-y-2">
            <button
              type="button"
              class="w-full py-2.5 px-4 rounded-xl text-sm font-medium text-[#1677ff] border border-[#1677ff]/40 hover:bg-blue-50 transition-colors"
              @click="showBatchPromoPanel = !showBatchPromoPanel"
            >
              {{ showBatchPromoPanel ? '收起优惠码' : '使用优惠码' }}
            </button>
            <div v-if="showBatchPromoPanel" class="flex flex-col sm:flex-row gap-2">
              <input
                v-model="batchPromoCode"
                type="text"
                placeholder="输入优惠码"
                autocomplete="off"
                class="flex-1 min-w-0 px-3 py-2.5 rounded-xl border border-gray-200 text-sm uppercase text-gray-900 placeholder-gray-400 focus:border-[#1677ff] focus:ring-2 focus:ring-[#1677ff]/20 outline-none"
                @input="onBatchPromoInput"
                @keyup.enter="applyBatchPromo"
              />
              <button
                type="button"
                :disabled="promoApplying || !batchPromoCode.trim()"
                class="shrink-0 py-2.5 px-4 rounded-xl text-sm font-medium text-white bg-[#1677ff] hover:bg-[#4096ff] disabled:opacity-50 disabled:cursor-not-allowed"
                @click="applyBatchPromo"
              >
                {{ promoApplying ? '应用中…' : '应用' }}
              </button>
            </div>
          </div>
          <p v-else class="text-left text-sm text-green-700 bg-green-50 rounded-xl px-3 py-2">
            已使用优惠码，已抵扣 <span class="font-semibold">{{ batchPromoRows }}</span> 条需付费数据
          </p>

          <button
            v-if="paymentURL && paymentPrice > 0"
            type="button"
            @click="handleAlipayClick"
            class="w-full py-4 px-6 rounded-xl font-medium text-white transition-colors bg-[#1677ff] hover:bg-[#4096ff]"
          >
            去支付宝付款 ¥{{ paymentPrice }}
          </button>
          
          <div class="text-sm text-gray-600">
            支付完成后自动继续处理文件
          </div>
          
          <button
            @click="handleCancelPayment"
            class="w-full text-gray-500 py-2 hover:text-gray-700 transition-colors"
          >
            取消
          </button>
        </div>
      </div>
    </div>
    
    <div class="max-w-4xl mx-auto">
      <!-- Header -->
      <div class="relative text-center mb-16">
        <div class="flex flex-wrap items-center justify-between gap-y-2 gap-x-3 mb-12 sm:mb-16 w-full text-left">
          <div class="flex flex-wrap items-center gap-1 justify-start min-w-0">
            <template v-if="!authUser">
              <button
                type="button"
                @click="openAuthModal('login')"
                class="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-white hover:bg-opacity-80 hover:shadow-md transition-all duration-200"
              >
                登录
              </button>
              <button
                type="button"
                @click="openAuthModal('register')"
                class="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition-all duration-200"
              >
                注册
              </button>
            </template>
            <template v-else>
              <div ref="userMenuRoot" class="relative inline-flex min-w-0">
                <button
                  type="button"
                  class="flex items-center gap-1.5 max-w-full px-3 py-2 rounded-xl text-sm font-medium text-gray-700 hover:bg-white hover:bg-opacity-80 hover:shadow-md transition-all duration-200"
                  :aria-expanded="userMenuOpen"
                  aria-haspopup="true"
                  @click.stop="userMenuOpen = !userMenuOpen"
                >
                  <span class="truncate max-w-[min(100%,12rem)] text-left" :title="userDisplayName(authUser)">{{
                    userDisplayName(authUser)
                  }}</span>
                  <svg
                    class="shrink-0 text-gray-500 transition-transform duration-200"
                    :class="{ 'rotate-180': userMenuOpen }"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>
                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <div
                    v-show="userMenuOpen"
                    class="absolute left-0 top-full z-[100] mt-1 min-w-[10rem] rounded-xl border border-gray-200/80 bg-white py-1 shadow-lg backdrop-blur-sm"
                    role="menu"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      class="flex w-full items-center px-4 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                      @click="goToApiKeys"
                    >
                      API Key
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      class="flex w-full items-center px-4 py-2.5 text-left text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                      @click="onLogoutFromMenu"
                    >
                      退出登录
                    </button>
                  </div>
                </transition>
              </div>
            </template>
          </div>
          <div class="flex flex-wrap items-center gap-1 justify-end sm:ml-auto">
            <button
              @click="goToApiDocs"
              class="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-white hover:bg-opacity-80 hover:shadow-md transition-all duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16"/><path d="M4 12h16"/><path d="M4 17h10"/></svg>
              HTTP 文档
            </button>
            <button
              @click="goToMcpDocs"
              class="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-white hover:bg-opacity-80 hover:shadow-md transition-all duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
              MCP 文档
            </button>
            <button
              @click="goToPricing"
              class="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-white hover:bg-opacity-80 hover:shadow-md transition-all duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
              价格
            </button>
            <button
              @click="goToOrders"
              class="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-white hover:bg-opacity-80 hover:shadow-md transition-all duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 16h6"/><path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14"/><path d="m7.5 4.27 9 5.15"/><polyline points="3.29 7 12 12 20.71 7"/><line x1="12" y1="22" x2="12" y2="12"/></svg>
              订单列表
            </button>
          </div>
        </div>
        <div class="inline-flex items-center justify-center w-16 h-16 gradient-button rounded-full mb-4 shadow-lg">
          <IconHash :size="32" class="text-white" />
        </div>
        <h1 class="text-3xl font-bold text-gray-900 mb-2">哈希查号</h1>
        <p class="text-gray-600">支持 MD5、SHA256 与国密 SM3 摘要的手机号查询与批量处理</p>
      </div>

      <!-- Tab Navigation -->
      <div class="flex glass-effect rounded-2xl p-2 mb-8 shadow-lg border border-white border-opacity-20">
        <router-link
          to="/single"
          :class="[
            'flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all duration-300',
            activeTab === 'single'
              ? 'bg-white text-blue-600 shadow-md'
              : 'text-gray-600 hover:text-gray-900'
          ]"
        >
          <IconSearch :size="20" />
          单个查询
        </router-link>
        <router-link
          to="/batch"
          :class="[
            'flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all duration-300',
            activeTab === 'batch'
              ? 'bg-white text-blue-600 shadow-md'
              : 'text-gray-600 hover:text-gray-900'
          ]"
        >
          <IconUpload :size="20" />
          批量处理
        </router-link>
      </div>

      <!-- Single Query Tab -->
      <transition name="fade">
        <div v-if="activeTab === 'single'" class="glass-effect rounded-3xl p-8 shadow-xl border border-white border-opacity-20">
          <div class="space-y-6">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-3">
                输入 Hash 值（MD5，或 64 位十六进制 SHA256 / 国密 SM3）
              </label>
              <div class="relative">
                <input
                  v-model="singleHash"
                  type="text"
                  :placeholder="singleDemoMd5"
                  class="w-full px-4 py-4 pr-12 bg-white bg-opacity-80 rounded-2xl border border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500 focus:ring-opacity-20 transition-all duration-300 text-gray-900 placeholder-gray-400 outline-none"
                />
                <IconHash class="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" :size="20" />
              </div>
              <transition name="slide-up">
                <div v-if="singleHash && hashError" class="mt-2 text-sm text-red-600">
                  <span class="font-medium">格式不正确：</span>{{ hashError }}
                </div>
              </transition>
            </div>

            <button
              @click="searchSingleHash"
              :disabled="!canSubmitSingleQuery || isSearching"
              class="w-full gradient-button text-white py-4 px-6 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 scale-hover"
            >
              <IconLoader v-if="isSearching" :size="20" />
              <IconSearch v-else :size="20" />
              {{ isSearching ? '查询中...' : '开始查询' }}
            </button>

            <transition name="slide-up">
              <div v-if="singleResult" class="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 border border-green-200">
                <div class="flex items-center gap-3 mb-3">
                  <IconPhone :size="24" class="text-green-600" />
                  <h3 class="font-semibold text-gray-900">查询结果</h3>
                </div>
                <div class="bg-white bg-opacity-80 rounded-xl p-4">
                  <div class="flex items-center justify-between gap-3">
                    <div class="text-2xl font-mono font-bold text-gray-900 break-all">{{ singleResult }}</div>
                    <button
                      v-if="singleResultCanCopy"
                      type="button"
                      class="shrink-0 gradient-button text-white py-2 px-3 rounded-lg text-xs font-semibold shadow-lg hover:shadow-xl transform transition-all duration-300 scale-hover"
                      @click="copySingleResult"
                    >
                      {{ singleResultCopied ? '已复制' : '复制' }}
                    </button>
                  </div>
                </div>
                <div
                  v-if="hashType || singleResultHint || singleQueryDurationMs !== null"
                  class="mt-3 space-y-2 text-left"
                >
                  <p v-if="hashType" class="text-sm text-gray-600">
                    哈希类型：<span class="font-medium text-blue-600">{{ hashType }}</span>
                  </p>
                  <p v-if="singleResultHint" class="text-sm text-gray-600 leading-relaxed">
                    {{ singleResultHint }}
                  </p>
                  <p v-if="singleQueryDurationMs !== null" class="text-xs text-gray-500">
                    查询耗时：{{ singleQueryDurationMs }} ms
                  </p>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </transition>

      <!-- Batch Processing Tab -->
      <transition name="fade">
        <div v-if="activeTab === 'batch'" class="glass-effect rounded-3xl p-8 shadow-xl border border-white border-opacity-20">
          <div class="space-y-6">
            <div v-if="!uploadedFile">
              <label class="block text-sm font-semibold text-gray-700 mb-3">
                上传CSV文件
              </label>
              <div
                @click="triggerFileInput"
                @drop="handleDrop"
                @dragover="handleDragOver"
                @dragleave="handleDragLeave"
                :class="[
                  'border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center cursor-pointer transition-all duration-300',
                  isDragOver ? 'drag-over' : 'hover:border-blue-500 hover:bg-blue-50 hover:bg-opacity-50'
                ]"
              >
                <IconUpload :size="48" class="text-gray-400 mx-auto mb-4" />
                <p class="text-gray-600 mb-2">点击选择CSV文件或拖拽文件到这里</p>
                <p class="text-sm text-gray-500">支持单列 CSV，每行为 MD5 或 64 位十六进制摘要（SHA256 / 国密 SM3）</p>
                <input
                  id="fileInput"
                  type="file"
                  accept=".csv"
                  @change="handleFileUpload"
                  class="hidden"
                />
              </div>
            </div>

            <transition name="slide-up">
              <div v-if="uploadedFile" class="space-y-3">
                <div class="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200">
                  <div class="flex items-center gap-3 mb-2">
                    <IconFileText :size="24" class="text-blue-600" />
                    <span class="font-semibold text-gray-900">{{ uploadedFile.name }}</span>
                  </div>
                  <p class="text-sm text-gray-600">文件大小: {{ formatFileSize(uploadedFile.size) }}</p>
                </div>
                <div
                  v-if="batchError"
                  role="alert"
                  class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 leading-relaxed"
                >
                  {{ batchError }}
                </div>
              </div>
            </transition>

            <transition name="slide-up">
              <div v-if="isProcessing" class="space-y-4">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-gray-700">处理进度</span>
                  <span class="text-sm font-bold text-blue-600">{{ progress }}%</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    class="h-full progress-bar rounded-full transition-all duration-300 ease-out"
                    :style="{ width: progress + '%' }"
                  />
                </div>
                <div class="flex items-center justify-center gap-2 text-blue-600 flex-wrap">
                  <IconLoader :size="20" />
                  <span class="font-medium">正在处理文件...请勿关闭浏览器或刷新页面</span>
                </div>
              </div>
            </transition>

            <transition name="slide-up">
              <div v-if="processedData" class="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 border border-green-200 space-y-4">
                <div class="flex items-center gap-3">
                  <IconCheckCircle :size="24" class="text-green-600" />
                  <h3 class="font-semibold text-gray-900">处理完成</h3>
                </div>
                <p class="text-gray-600">
                  共处理 <span class="font-bold text-green-600">{{ processedTotal }}</span> 条，
                  查得 <span class="font-bold text-blue-600">{{ processedFound }}</span> 条
                </p>
                <button
                  @click="downloadProcessedFile"
                  class="w-full gradient-green text-white py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform transition-all duration-300 flex items-center justify-center gap-2 scale-hover"
                >
                  <IconDownload :size="20" />
                  下载处理结果
                </button>
              </div>
            </transition>
          </div>
        </div>
      </transition>

      <!-- Footer -->
      <div class="mt-12 text-center text-gray-500 text-sm">
        <p>支持 MD5、SHA256 与国密 SM3 摘要解析 • 安全可靠的数据处理</p>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import AuthModal from '../components/AuthModal.vue';
import AnnouncementBar from '../components/AnnouncementBar.vue';
import AnnouncementModal from '../components/AnnouncementModal.vue';
import IconHash from '../components/icons/IconHash.vue';
import IconSearch from '../components/icons/IconSearch.vue';
import IconUpload from '../components/icons/IconUpload.vue';
import IconDownload from '../components/icons/IconDownload.vue';
import IconPhone from '../components/icons/IconPhone.vue';
import IconFileText from '../components/icons/IconFileText.vue';
import IconCheckCircle from '../components/icons/IconCheckCircle.vue';
import IconLoader from '../components/icons/IconLoader.vue';
import axios from 'axios';
import { isValidHash, getHashError } from '../utils/hash.js';
import { md5Hex, randomMainlandMobile } from '../utils/md5.js';
import { FREE_BATCH_LIMIT, BATCH_PRICE_PER_ROW } from '../utils/constants.js';
import { isSuccessResponse, extractData, handleApiError, getErrorMessage, BusinessCode } from '../utils/api.js';
import { getUser, getToken, clearAuth, isLoggedIn, onAuthChanged, userDisplayName } from '../utils/auth.js';

// 定义props
const props = defineProps({
  defaultTab: {
    type: String,
    default: 'single'
  }
});

const router = useRouter();
const route = useRoute();

// 响应式数据
const activeTab = ref(props.defaultTab);
const singleHash = ref('');
/** 随机手机号 MD5，作 placeholder；输入为空时用于查询 */
const singleDemoMd5 = ref(md5Hex(randomMainlandMobile()));

function refreshSingleDemoMd5() {
  singleDemoMd5.value = md5Hex(randomMainlandMobile());
}
const singleResult = ref('');
const singleResultHint = ref('');
const singleQueryDurationMs = ref(null);
const singleResultCanCopy = ref(false);
const singleResultCopied = ref(false);
const isSearching = ref(false);
const uploadedFile = ref(null);
/** 批量流程错误（上传校验、处理、下载等），展示在已选文件下方，不使用顶部浮层 */
const batchError = ref('');
const isProcessing = ref(false);
const progress = ref(0);
const processedData = ref(null);
const isDragOver = ref(false);
const processedTotal = ref(0);
const processedFound = ref(0);
const messageTimer = ref(null);
const message = ref('');
const messageType = ref('info');
const fileRowCount = ref(0);
const batchPurchaseToken = ref('');
// 支付相关变量
const showPaymentDialog = ref(false);
const paymentURL = ref('');
const pendingOrderId = ref('');
const paymentPrice = ref(0);
let paymentPollingTimer = null;
/** 批量支付弹窗：优惠码 */
const batchPromoCode = ref('');
const showBatchPromoPanel = ref(false);
const batchPromoApplied = ref(false);
const batchPromoRows = ref(0);
const promoApplying = ref(false);

function onBatchPromoInput(e) {
  batchPromoCode.value = e.target.value.toUpperCase();
}

function resetBatchPromoState() {
  batchPromoCode.value = '';
  showBatchPromoPanel.value = false;
  batchPromoApplied.value = false;
  batchPromoRows.value = 0;
  promoApplying.value = false;
}

const authUser = ref(null);
function syncAuth() {
  authUser.value = isLoggedIn() ? getUser() : null;
}

function onAuthSuccess(payload) {
  syncAuth();
  if (payload?.kind === 'login') {
    showMessage('登录成功', 'success');
  }
}
let unsubscribeAuth = null;

const showAuthModal = ref(false);
const authInitialTab = ref('login');
const authRedirectPath = ref('/single');

const userMenuOpen = ref(false);
const userMenuRoot = ref(null);

function closeUserMenu() {
  userMenuOpen.value = false;
}

function onUserMenuDocClick(e) {
  if (!userMenuOpen.value || !userMenuRoot.value) return;
  if (!userMenuRoot.value.contains(e.target)) {
    closeUserMenu();
  }
}

function onLogoutFromMenu() {
  closeUserMenu();
  logout();
}

function openAuthModal(tab) {
  authInitialTab.value = tab === 'register' ? 'register' : 'login';
  authRedirectPath.value = route.fullPath;
  showAuthModal.value = true;
}

/** 批量处理需登录；未登录则打开登录弹窗 */
function requireLoginForBatch() {
  if (isLoggedIn()) return true;
  openAuthModal('login');
  showMessage('请先登录后再上传文件进行批量处理', 'info');
  return false;
}

function tryOpenAuthFromQuery() {
  const a = route.query.auth;
  if (a !== 'login' && a !== 'register') return;
  authInitialTab.value = a;
  const r = route.query.redirect;
  authRedirectPath.value = typeof r === 'string' && r.startsWith('/') ? r : route.path || '/single';
  showAuthModal.value = true;
  const q = { ...route.query };
  delete q.auth;
  delete q.redirect;
  nextTick(() => {
    router.replace({ path: route.path, query: q });
  });
}

let fileID = null; // Global fileID variable
let ws = null;

const showMessage = (msg, type = 'info') => {
  if (messageTimer.value) {
    clearTimeout(messageTimer.value);
  }
  message.value = msg;
  messageType.value = type;
  messageTimer.value = setTimeout(() => { 
    message.value = '';
    messageTimer.value = null;
   }, 3000);
};

const onBeforeUnload = (e) => {
  if (!isProcessing.value) return;
  e.preventDefault();
  e.returnValue = '';
};

onMounted(async () => {
  syncAuth();
  unsubscribeAuth = onAuthChanged(syncAuth);
  tryOpenAuthFromQuery();
  document.addEventListener('click', onUserMenuDocClick);
  window.addEventListener('beforeunload', onBeforeUnload);
});

onUnmounted(() => {
  document.removeEventListener('click', onUserMenuDocClick);
  window.removeEventListener('beforeunload', onBeforeUnload);
  if (unsubscribeAuth) {
    unsubscribeAuth();
    unsubscribeAuth = null;
  }
  if (paymentPollingTimer) {
    clearInterval(paymentPollingTimer);
    paymentPollingTimer = null;
  }
});

// 监听路由变化，更新 active tab；从批量切回单个时换新示例 MD5（占位留空查询用）
watch(() => route.path, (newPath, oldPath) => {
  if (newPath === '/single') {
    activeTab.value = 'single';
    if (oldPath === '/batch') {
      refreshSingleDemoMd5();
    }
  } else if (newPath === '/batch') {
    activeTab.value = 'batch';
  }
}, { immediate: true });

watch(
  () => route.query.auth,
  (auth) => {
    if (auth === 'login' || auth === 'register') tryOpenAuthFromQuery();
  }
);

// 计算属性
// 后端查询返回的 hash 类型（MD5 / SHA256 / SM3），查询前为空
const hashType = ref('');

// 计算hash是否有效（有输入时校验输入；留空由示例哈希兜底）
const isHashValid = computed(() => {
  const t = singleHash.value.trim();
  if (!t) return true;
  return isValidHash(singleHash.value);
});

const canSubmitSingleQuery = computed(() => {
  const t = singleHash.value.trim();
  if (!t) return singleDemoMd5.value.length === 32;
  return isValidHash(singleHash.value);
});

// hash格式错误提示（有输入时才校验）
const hashError = computed(() => {
  if (!singleHash.value.trim()) return '';
  return getHashError(singleHash.value);
});

// 方法
const setActiveTab = (tab) => {
  if (tab === 'single') {
    router.push('/single');
  } else if (tab === 'batch') {
    router.push('/batch');
  }
};

// detectHashType 已移除，使用工具函数 getHashType

const searchSingleHash = async () => {
  const raw = singleHash.value.trim();
  const hashToQuery = raw || singleDemoMd5.value;

  if (!isValidHash(hashToQuery)) {
    showMessage('Hash 格式不正确，请输入有效的 MD5（32 个十六进制字符）或 SHA256 / 国密 SM3（64 个十六进制字符）', 'error');
    return;
  }

  isSearching.value = true;
  singleResult.value = '';
  singleResultHint.value = '';
  singleQueryDurationMs.value = null;
  singleResultCanCopy.value = false;
  singleResultCopied.value = false;
  hashType.value = '';
  try {
    const res = await axios.post('/api/hash-to-phone', {
      hash: hashToQuery
    });

    if (isSuccessResponse(res)) {
      const data = extractData(res);
      if (data && data.found) {
        singleResult.value = data.phone;
        singleResultHint.value = '';
        singleResultCanCopy.value = true;
        singleQueryDurationMs.value = typeof data.duration_ms === 'number' ? data.duration_ms : null;
        hashType.value = typeof data.hash_type === 'string' ? data.hash_type : '';
      } else {
        singleQueryDurationMs.value = null;
        singleResult.value = '未找到对应的手机号';
        singleResultHint.value =
          '系统已包含全量手机号数据，请检查该 hash 是否由纯手机号直接经 MD5、SHA256 或国密 SM3 计算（未做加盐、拼接前后缀、二次加密等处理）。';
        singleResultCanCopy.value = false;
      }
    } else {
      const errorInfo = handleApiError({ response: res });
      singleResult.value = errorInfo.message || '查询失败';
      singleResultHint.value = '';
      singleResultCanCopy.value = false;
    }
  } catch (err) {
    const errorInfo = handleApiError(err);
    if (errorInfo.code === BusinessCode.TOO_MANY_REQUESTS) {
      showMessage('请求过于频繁，请稍后重试', 'error');
    } else {
      singleResult.value = errorInfo.message || '查询失败';
      singleResultHint.value = '';
      singleResultCanCopy.value = false;
    }
  } finally {
    isSearching.value = false;
  }
};

const copySingleResult = async () => {
  if (!singleResultCanCopy.value || !singleResult.value) return;
  const text = singleResult.value;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (!ok) throw new Error('copy failed');
    }
    singleResultCopied.value = true;
    showMessage('手机号已复制到剪贴板', 'info');
    setTimeout(() => {
      singleResultCopied.value = false;
    }, 1500);
  } catch (err) {
    showMessage('复制失败，请手动复制', 'error');
  }
};

const handleFileUpload = async (event) => {
  const file = event.target.files[0];
  if (file && file.type === 'text/csv') {
    if (!requireLoginForBatch()) {
      event.target.value = '';
      return;
    }
    uploadedFile.value = file;
    batchError.value = '';
    processedData.value = null;
    processedTotal.value = 0;
    processedFound.value = 0;
    progress.value = 0;
    fileRowCount.value = 0;
    fileID = null;
    showPaymentDialog.value = false;
    
    // 立即上传文件
    await uploadFileAndGetPaymentInfo(file);
  }
};

const handleDrop = async (event) => {
  event.preventDefault();
  isDragOver.value = false;
  const files = event.dataTransfer.files;
  if (files.length > 0 && files[0].type === 'text/csv') {
    if (!requireLoginForBatch()) {
      return;
    }
    uploadedFile.value = files[0];
    batchError.value = '';
    processedData.value = null;
    processedTotal.value = 0;
    processedFound.value = 0;
    progress.value = 0;
    fileRowCount.value = 0;
    fileID = null;
    showPaymentDialog.value = false;
    
    // 立即上传文件
    await uploadFileAndGetPaymentInfo(files[0]);
  }
};

const handleDragOver = (event) => {
  event.preventDefault();
  isDragOver.value = true;
};

const handleDragLeave = () => {
  isDragOver.value = false;
};

const triggerFileInput = () => {
  if (!requireLoginForBatch()) return;
  document.getElementById('fileInput').click();
};

// 上传文件并获取支付信息
const uploadFileAndGetPaymentInfo = async (file) => {
  batchError.value = '';
  isProcessing.value = true;
  try {
    const formData = new FormData();
    formData.append('file', file);

    const res = await axios.post('/api/upload', formData);
    
    if (isSuccessResponse(res)) {
      const data = extractData(res);
      fileID = data.file_id;
      fileRowCount.value = data.row_count;
      batchError.value = '';
      
      if (data.need_payment) {
        // 需要支付，显示支付对话框（直接将 payment_url 作为图片地址展示）
        resetBatchPromoState();
        paymentURL.value = data.payment_url;
        paymentPrice.value = data.price;
        pendingOrderId.value = data.order_id;
        showPaymentDialog.value = true;
        startPaymentPolling();
      } else {
        // 不需要支付，使用返回的 token 直接处理文件
        await processFileAfterPayment(data.token);
      }
    } else {
      const errorInfo = handleApiError({ response: res });
      batchError.value = errorInfo.message || '文件上传失败';
    }
  } catch (err) {
    const errorInfo = handleApiError(err);
    batchError.value = errorInfo.message || '文件上传失败';
  } finally {
    isProcessing.value = false;
  }
};

// 支付成功后处理文件（body.token 为订单凭据，与登录 JWT 无关）
const processFileAfterPayment = async (orderToken) => {
  if (!fileID) return;
  
  batchError.value = '';
  isProcessing.value = true;
  progress.value = 0;
  processedData.value = null;
  processedTotal.value = 0;
  processedFound.value = 0;
  const SSE_CONNECT_TIMEOUT_MS = 15000;
  const SSE_IDLE_TIMEOUT_MS = 20000;
  let streamAbortReason = '';
  let connectTimeoutTimer = null;
  let idleTimeoutTimer = null;
  const controller = new AbortController();
  const clearSSETimeouts = () => {
    if (connectTimeoutTimer) {
      clearTimeout(connectTimeoutTimer);
      connectTimeoutTimer = null;
    }
    if (idleTimeoutTimer) {
      clearTimeout(idleTimeoutTimer);
      idleTimeoutTimer = null;
    }
  };
  const resetIdleTimeout = () => {
    if (idleTimeoutTimer) {
      clearTimeout(idleTimeoutTimer);
    }
    idleTimeoutTimer = setTimeout(() => {
      streamAbortReason = '文件处理连接已中断：长时间未收到进度，请重试';
      controller.abort();
    }, SSE_IDLE_TIMEOUT_MS);
  };
  
  try {
    const authToken = getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (authToken) headers.Authorization = `Bearer ${authToken}`;

    const orderCredential = orderToken || batchPurchaseToken.value;
    if (!orderCredential) {
      batchError.value = '缺少订单凭据，请重新上传或完成支付';
      return;
    }

    connectTimeoutTimer = setTimeout(() => {
      streamAbortReason = '连接处理服务超时，请稍后重试';
      controller.abort();
    }, SSE_CONNECT_TIMEOUT_MS);

    // 使用 fetch 发送 POST 请求，接收 SSE 流
    const response = await fetch('/api/process-file', {
      method: 'POST',
      headers,
      signal: controller.signal,
      body: JSON.stringify({
        file_id: fileID,
        token: orderCredential,
      })
    });
    if (connectTimeoutTimer) {
      clearTimeout(connectTimeoutTimer);
      connectTimeoutTimer = null;
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      const errorInfo = handleApiError({ response: { data: errorData, status: response.status } });
      batchError.value = errorInfo.message || '文件处理失败';
      return;
    }
    
    // 检查响应类型是否为 event-stream
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('text/event-stream')) {
      // 如果不是 SSE，尝试解析 JSON 响应（兼容旧接口）
      const data = await response.json();
      if (isSuccessResponse({ data })) {
        showMessage('文件处理已开始', 'success');
      }
      return;
    }
    
    // 读取 SSE 流
    resetIdleTimeout();
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    
    await new Promise((resolve, reject) => {
      const readStream = () => {
        reader.read().then(({ done, value }) => {
          if (done) {
            if (processedData.value) {
              resolve();
            } else if (progress.value >= 100) {
              // 兼容旧后端：仅有 progress 100、无 completed 事件
              processedData.value = true;
              showMessage('文件处理完成！', 'success');
              resolve();
            } else {
              reject(new Error('文件处理连接已结束，请检查后端服务是否正常'));
            }
            return;
          }
          resetIdleTimeout();
          
          // 统一 CRLF/LF，并按 SSE 事件边界（空行）解析，避免分片导致一次性消费
          buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
          const events = buffer.split('\n\n');
          buffer = events.pop() || ''; // 保留最后不完整事件

          for (const eventBlock of events) {
            const dataLine = eventBlock
              .split('\n')
              .find((line) => line.startsWith('data:'));
            if (!dataLine) continue;
            try {
              const jsonStr = dataLine.slice(5).trim(); // 移除 data: 前缀
              const msg = JSON.parse(jsonStr);

              // 处理不同类型的消息
              if (msg.type === 'started') {
                // 处理开始
                resetIdleTimeout();
              } else if (msg.type === 'completed') {
                if (msg.total !== undefined) {
                  processedTotal.value = msg.total;
                }
                if (msg.found !== undefined) {
                  processedFound.value = msg.found;
                }
                processedData.value = true;
                showMessage('文件处理完成！', 'success');
                clearSSETimeouts();
                resolve();
                return;
              } else if (msg.type === 'error') {
                batchError.value = msg.message || '文件处理失败';
                clearSSETimeouts();
                reject(new Error(msg.message || '文件处理失败'));
                return;
              } else {
                // 进度更新（type 为 process 或未指定；完成以 completed 事件为准）
                if (msg.progress !== undefined) {
                  progress.value = msg.progress;
                }
                if (msg.total !== undefined) {
                  processedTotal.value = msg.total;
                }
                if (msg.found !== undefined) {
                  processedFound.value = msg.found;
                }
              }
            } catch (err) {
              console.error('解析SSE消息失败:', err);
            }
          }
          
          readStream();
        }).catch((err) => {
          console.error('读取SSE流失败:', err);
          clearSSETimeouts();
          // 如果进度已完成，不视为错误
          if (progress.value >= 100) {
            resolve();
          } else {
            reject(err);
          }
        });
      };
      
      readStream();
    });
    
  } catch (err) {
    const info = handleApiError(err);
    const fromErr = streamAbortReason || (typeof err?.message === 'string' ? err.message : '');
    if (!batchError.value) {
      batchError.value = fromErr || info.message || '文件处理失败';
    }
  } finally {
    clearSSETimeouts();
    isProcessing.value = false;
  }
};


const downloadProcessedFile = async () => {
  if (!fileID) return;
  try {
    const res = await axios.get('/api/download', {
      params: { file_id: fileID },
      responseType: 'blob',
    });
    
    // 检查响应类型，如果是JSON错误响应，需要解析
    const contentType = res.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      const text = await new Blob([res.data]).text();
      const errorData = JSON.parse(text);
      const errorInfo = handleApiError({ response: { data: errorData, status: res.status } });
      batchError.value = errorInfo.message || '下载处理结果失败';
      return;
    }
    
    // 从响应头获取文件名
    let filename = 'processed_phone_data.csv';
    const disposition = res.headers['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
    if (filenameMatch && filenameMatch[1]) {
      filename = decodeURIComponent(filenameMatch[1]);
    }

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    const errorInfo = handleApiError(err);
    batchError.value = errorInfo.message || '下载处理结果失败';
  }
};

const formatFileSize = (bytes) => {
  return (bytes / 1024).toFixed(2) + ' KB';
};

const goToOrders = () => {
  router.push('/orders');
};

const goToPricing = () => {
  router.push('/pricing');
};

const goToApiDocs = () => {
  router.push('/docs/api');
};

const goToMcpDocs = () => {
  router.push('/docs/mcp');
};

const goToApiKeys = () => {
  closeUserMenu();
  router.push('/settings/api-keys');
};


const logout = () => {
  closeUserMenu();
  clearAuth();
  syncAuth();
  showMessage('已退出登录', 'info');
};



const startPaymentPolling = () => {
  if (paymentPollingTimer) clearInterval(paymentPollingTimer);
  paymentPollingTimer = setInterval(async () => {
    if (!pendingOrderId.value) return;
    try {
      // 查询订单支付状态
      const res = await axios.get('/api/order/status', { 
        params: { order_id: pendingOrderId.value } 
      });
      
      if (isSuccessResponse(res)) {
        const data = extractData(res);
        if (data?.paid || res.data?.paid) {
          clearInterval(paymentPollingTimer);
          showPaymentDialog.value = false;
          batchPurchaseToken.value = data?.token || res.data?.token; // 获取支付后的token
          showMessage('支付成功！开始处理文件...', 'success');
          await processFileAfterPayment(batchPurchaseToken.value); // 支付完成，开始处理文件
        }
      }
    } catch (err) {
      console.error('查询支付状态失败:', err);
    }
  }, 2000); // 每2秒轮询一次
};

const handleAlipayClick = () => {
  if (paymentURL.value) window.open(paymentURL.value, '_blank');
};

const applyBatchPromo = async () => {
  const code = batchPromoCode.value.trim().toUpperCase();
  if (!code || !pendingOrderId.value) {
    showMessage('请输入优惠码', 'error');
    return;
  }
  promoApplying.value = true;
  try {
    const res = await axios.post('/api/order/apply-promo', {
      order_no: pendingOrderId.value,
      code
    });
    if (isSuccessResponse(res)) {
      const data = extractData(res);
      paymentPrice.value = data.price;
      batchPromoApplied.value = true;
      batchPromoRows.value = typeof data.promo_rows === 'number' ? data.promo_rows : 0;
      if (data.need_payment) {
        paymentURL.value = data.payment_url || '';
        showMessage('优惠码已应用', 'info');
      } else {
        if (paymentPollingTimer) {
          clearInterval(paymentPollingTimer);
          paymentPollingTimer = null;
        }
        showPaymentDialog.value = false;
        resetBatchPromoState();
        showMessage('优惠码已应用，开始处理文件', 'info');
        await processFileAfterPayment(data.token);
      }
    } else {
      const errorInfo = handleApiError({ response: res });
      showMessage(errorInfo.message || '应用优惠码失败', 'error');
    }
  } catch (err) {
    const errorInfo = handleApiError(err);
    showMessage(errorInfo.message || '应用优惠码失败', 'error');
  } finally {
    promoApplying.value = false;
  }
};

const handleCancelPayment = async () => {
  if (pendingOrderId.value) {
    try {
      await axios.post('/api/order/cancel', null, {
        params: { order_id: pendingOrderId.value }
      });
    } catch (err) {
      console.error('取消订单失败:', err);
    }
  }
  showPaymentDialog.value = false;
  if (paymentPollingTimer) {
    clearInterval(paymentPollingTimer);
    paymentPollingTimer = null;
  }
  resetBatchPromoState();
  paymentURL.value = '';
  pendingOrderId.value = '';
  paymentPrice.value = 0;
  uploadedFile.value = null;
  batchError.value = '';
  processedData.value = null;
  processedTotal.value = 0;
  processedFound.value = 0;
  progress.value = 0;
  fileRowCount.value = 0;
};

defineExpose({
  activeTab,
  singleHash,
  singleResult,
  isSearching,
  uploadedFile,
  isProcessing,
  progress,
  processedData,
  isDragOver,
  hashType,
  setActiveTab,
  searchSingleHash,
  handleFileUpload,
  handleDrop,
  handleDragOver,
  handleDragLeave,
  triggerFileInput,
  downloadProcessedFile,
  formatFileSize,
  fileRowCount,
  // 支付相关
  showPaymentDialog,
  paymentURL,
  pendingOrderId,
  paymentPrice,
  startPaymentPolling,
  handleCancelPayment
});
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.3s ease;
}
.slide-up-enter-from, .slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.glass-effect {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.gradient-button {
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
}

.gradient-green {
  background: linear-gradient(135deg, #10b981 0%, #3b82f6 100%);
}

.scale-hover:hover {
  transform: scale(1.02);
}

.progress-bar {
  background: linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%);
}

.drag-over {
  border-color: #3b82f6 !important;
  background-color: rgba(59, 130, 246, 0.1) !important;
}
</style> 